import { createContext, useContext, useState, useCallback, useRef } from 'react';

const NotificationContext = createContext(null);

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const counterRef = useRef(0);
  const timersRef = useRef({});

  const removeNotification = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const addNotification = useCallback(({ type, message, duration, action }) => {
    const id = ++counterRef.current;
    setNotifications(prev => [...prev, { id, type, message, action }]);

    const autoMs = duration !== undefined ? duration : (type === 'error' ? null : 5000);
    if (autoMs) {
      timersRef.current[id] = setTimeout(() => removeNotification(id), autoMs);
    }

    return id;
  }, [removeNotification]);

  const showSuccess = useCallback(
    (message, opts = {}) => addNotification({ type: 'success', message, ...opts }),
    [addNotification]
  );

  const showError = useCallback(
    (message, opts = {}) => addNotification({ type: 'error', message, ...opts }),
    [addNotification]
  );

  const showInfo = useCallback(
    (message, opts = {}) => addNotification({ type: 'info', message, duration: 8000, ...opts }),
    [addNotification]
  );

  return (
    <NotificationContext.Provider value={{
      notifications,
      showSuccess,
      showError,
      showInfo,
      removeNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}
